import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import style from 'assets/jss/material-dashboard-pro-react/views/services/FixedBusinessBroadband/filesStyleFBB.js';
import { TextField, makeStyles } from '@material-ui/core';
import FileUpload from 'components/CustomUpload/FileUpload';
import CustomTooltip from 'components/CustomTooltip/CustomTooltip';
import { Caption, CaptionBold, HeadingM, HeadingMBold } from 'components/Typography/Typography';
import CustomButton from 'components/CustomButtons/Button';
import Delete from '@material-ui/icons/DeleteOutlined';
import Circle from '@material-ui/icons/CheckCircleOutlined';
import KarokiePicture from 'assets/img/karokie.svg';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { useSelector } from 'react-redux';
import { getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(style);

interface IFilesProps {
	handleAddKarokie: (file: { content?: string; name?: string }) => void;
	karokie: { content?: string; name?: string };
	onChange?: (name: keyof types.services.fixed.IContactDetails, value: string | boolean) => void;
	showWorkingNumber?: Boolean;
	workingNumber?: string;
	setNextStepDisabled?: (nextStepDisabled: boolean) => void;
}

const FilesTabFBB = ({
	handleAddKarokie,
	karokie,
	showWorkingNumber = false,
	workingNumber = '',
	onChange = () => {},
	setNextStepDisabled,
}: IFilesProps) => {
	// this component get focussed - for accessibility 
	const ref: RefObject<HTMLDivElement> = useRef(null);
	useEffect(() => {
		if (ref.current) ref.current.focus();
	}, []);
	const classes = useStyles();
	const fiberProLabels = useSelector(getFiberProLabels);
	const getValue = (event: React.FormEvent<HTMLInputElement>) => (event.target as HTMLInputElement).value;
	const workingNumberValid = useMemo(() => {
		if(workingNumber.length > 0)
			return workingNumber.trim().length > 6 && /2[2-6]\d{6}$/.test(workingNumber);
		return true
	}, [workingNumber]);
	useEffect(() => {
		if (setNextStepDisabled) setNextStepDisabled(!workingNumberValid);
	}, [workingNumberValid]);
	return (
		<GridContainer justify="flex-start">
			<GridItem xs={8} md={8} lg={8} justify="flex-start">
				{showWorkingNumber && (
					<div ref={ref}>
						<TextField
							onChange={(event: any) => onChange('workingNumber', getValue(event).replace(/[^0-9]$/, ''))}
							value={workingNumber}
							inputProps={{ maxLength: 8, 'data-testid': 'workingNumber' }}
							fullWidth
							variant="outlined"
							placeholder={fiberProLabels.fixedLineNumberEnter}
							error={workingNumber.length ? !workingNumberValid : false}
							helperText={
								workingNumber.length && !workingNumberValid
									? fiberProLabels.notOmanNoErr
									: ''
							}
						/>
					</div>
				)}
				<div className={classes.filesTitleWrapper}>
					<HeadingMBold>{fiberProLabels.uploadFile}</HeadingMBold>
				</div>
				{karokie.content ? (
					<div className={classNames(classes.upload, classes.file)}>
						<Caption color="disabled-light">{fiberProLabels.locationCor}</Caption>
						<CustomButton
							color="tertiary-light"
							id="deleteButton"
							className={classes.deleteIcon}
							justIcon
							size="lg"
							onClick={() => handleAddKarokie({ content: undefined, name: undefined })}
						>
							<Delete />
						</CustomButton>
						<div className={classes.fileNameContainer}>
							<Circle className={classes.success} />
							<CaptionBold inline className={classes.filename} disableMargin>
								{karokie.name}
							</CaptionBold>
						</div>
					</div>
				) : (
					<div className={classes.uploadItem}>
						<FileUpload
							iconPosition="right"
							size="md"
							fileMaxSize={2.5 * 1024 * 1024}
							className={classes.upload}
							text={fiberProLabels.locationCor}
							onFileChange={(file: string) => {
								handleAddKarokie({ content: file });
							}}
							onMetadataChange={(data) => {
								handleAddKarokie({ name: data.name });
							}}
							showUploadButton
						/>
						<CustomTooltip>{fiberProLabels.krookiToolTip}</CustomTooltip>
					</div>
				)}
				<div className={classes.karokieWrapper}>
					<img alt="karokie document" src={KarokiePicture} className={classes.karokie} />
				</div>
			</GridItem>
		</GridContainer>
	);
};

export default FilesTabFBB;
